import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import { uploadFile, cdnUrl, isUploadConfigured, searchYouTube } from '../data/uploadService';
import {
  useSongLibrary,
  addLibrarySong,
  buildLibrarySongKey,
  renameLibrarySong,
  trashSong,
  restoreSong,
  vaporizeSong,
  isTrashed,
  purgeDaysLeft,
  requestWaveform,
} from '../data/firestore';
import { DownloadButton, InlineRename, TrashControls } from './AssetActions';

const fmtBytes = (n) => {
  if (!n) return '';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};
const fmtDur = (s) => {
  if (!s) return '';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

// Decode just to read the duration of a picked audio file.
function readDuration(file) {
  return new Promise((resolve) => {
    const el = document.createElement('audio');
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      resolve(Number.isFinite(el.duration) ? el.duration : 0);
      URL.revokeObjectURL(el.src);
    };
    el.onerror = () => resolve(0);
    el.src = URL.createObjectURL(file);
  });
}

function Sparkline({ peaks, width = 120, height = 28 }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !peaks || !peaks.length) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = width * dpr;
    cv.height = height * dpr;
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#14e0c8';
    const mid = height / 2;
    for (let x = 0; x < width; x += 1) {
      const v = peaks[Math.floor((x / width) * peaks.length)] || 0;
      const h = Math.max(1, v * (height - 2));
      ctx.fillRect(x, mid - h / 2, 1, h);
    }
  }, [peaks, width, height]);
  return <canvas ref={ref} style={{ width, height, display: 'block' }} />;
}

function WaveStatus({ song }) {
  const st = song.waveform?.status || (song.peaks?.length ? 'ready' : 'none');
  if (st === 'ready' && song.peaks?.length) return <Sparkline peaks={song.peaks} />;
  if (st === 'queued' || st === 'analyzing') {
    return <span style={{ color: 'var(--surf)', fontSize: 12 }}>analyzing…</span>;
  }
  if (st === 'error') {
    return <span style={{ color: 'var(--accent)', fontSize: 12 }} title={song.waveform?.message}>failed</span>;
  }
  return (
    <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => requestWaveform(song.id)}>
      Analyze
    </button>
  );
}

// Browse/search the reusable song library and pick one (onPick), upload a new
// track, or add a YouTube track as a candidate. Waveforms are extracted by the
// backend on demand and cached on each song.
export default function SongLibraryModal({ onClose, onPick }) {
  const configured = isUploadConfigured();
  const { docs: songs } = useSongLibrary();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState('');
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const [ytQ, setYtQ] = useState('');
  const [ytResults, setYtResults] = useState(null);
  const [ytBusy, setYtBusy] = useState(false);

  const [showTrash, setShowTrash] = useState(false);
  const trashedSongs = useMemo(() => (songs || []).filter(isTrashed), [songs]);
  const filtered = useMemo(() => {
    const list = (songs || []).filter((s) => !isTrashed(s));
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((s) => (s.label || '').toLowerCase().includes(needle));
  }, [songs, q]);
  const listToShow = showTrash ? trashedSongs : filtered;

  const uploadNew = async (file) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    setProgress(0);
    try {
      const key = buildLibrarySongKey(file.name);
      const durationSec = await readDuration(file);
      await uploadFile(file, {
        key,
        contentType: file.type || 'audio/mpeg',
        bucket: 'movies',
        onProgress: setProgress,
      });
      await addLibrarySong({
        label: label.trim() || file.name.replace(/\.[^.]+$/, ''),
        url: cdnUrl(key),
        storageKey: key,
        durationSec,
        sizeBytes: file.size,
        autoAnalyze: true,
      });
      setLabel('');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const runYt = async (e) => {
    e?.preventDefault();
    if (!ytQ.trim()) return;
    setYtBusy(true);
    setError(null);
    try {
      setYtResults(await searchYouTube(ytQ.trim()));
    } catch (err) {
      setError(err.message || String(err));
      setYtResults([]);
    } finally {
      setYtBusy(false);
    }
  };

  const addYt = async (r) => {
    try {
      await addLibrarySong({
        label: r.title,
        source: 'youtube',
        videoId: r.videoId,
        thumb: r.thumb || '',
        durationSec: r.durationSec || 0,
        autoAnalyze: true,
      });
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  return (
    <Modal title="Song library" onClose={busy ? () => {} : onClose} maxWidth={620}>
      <div className="cs-field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          className="cs-input"
          placeholder="Search your songs…"
          value={q}
          disabled={showTrash}
          onChange={(e) => setQ(e.target.value)}
        />
        {trashedSongs.length > 0 && (
          <button
            className={`cs-btn cs-btn-sm ${showTrash ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => setShowTrash((s) => !s)}
          >
            🗑 Trash ({trashedSongs.length})
          </button>
        )}
      </div>

      <div className="cs-field" style={{ borderTop: '1px solid #2a2a2a', paddingTop: 14 }}>
        <label>Add a new song to the library</label>
        <input
          className="cs-input"
          placeholder="Label (optional — defaults to file name)"
          value={label}
          disabled={busy}
          onChange={(e) => setLabel(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          disabled={busy || !configured}
          onChange={(e) => uploadNew(e.target.files?.[0])}
        />
        {busy && (
          <div className="cs-progress" style={{ marginTop: 8 }}>
            <div style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        )}
        {!configured && (
          <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 6 }}>
            Upload backend not configured.
          </div>
        )}
      </div>

      <div className="cs-field" style={{ borderTop: '1px solid #2a2a2a', paddingTop: 14 }}>
        <label>Add from YouTube</label>
        <form onSubmit={runYt} style={{ display: 'flex', gap: 8 }}>
          <input
            className="cs-input"
            placeholder="Song, artist, mix…"
            value={ytQ}
            onChange={(e) => setYtQ(e.target.value)}
          />
          <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={ytBusy}>
            {ytBusy ? '…' : 'Search'}
          </button>
        </form>
        {ytResults && ytResults.length > 0 && (
          <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
            {ytResults.map((r) => (
              <div key={r.videoId} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 2px', borderBottom: '1px solid #222' }}>
                {r.thumb && <img src={r.thumb} alt="" style={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 4 }} />}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>{[r.channel, fmtDur(r.durationSec)].filter(Boolean).join(' · ')}</div>
                </div>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => addYt(r)}>+ Library</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="cs-empty" style={{ marginTop: 8 }}>{error}</div>}

      <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 8 }}>
        {songs === null ? (
          <div>Loading…</div>
        ) : listToShow.length === 0 ? (
          <div className="cs-empty">
            {showTrash ? 'Trash is empty.' : `No songs${q ? ' match' : ' yet'}.`}
          </div>
        ) : (
          listToShow.map((s) => {
            const trashed = isTrashed(s);
            return (
              <div
                key={s.id}
                className={trashed ? 'cs-trashed' : ''}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: '1px solid #222' }}
              >
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>{s.source === 'youtube' ? '▶' : '♪'}</span>
                    <InlineRename
                      name={s.label}
                      onSave={(n) => renameLibrarySong(s.id, n)}
                      style={{ fontWeight: 600, minWidth: 0 }}
                    />
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                    {[s.source === 'youtube' ? 'YouTube' : fmtBytes(s.sizeBytes), fmtDur(s.durationSec)].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {trashed ? (
                  <TrashControls
                    trashed
                    daysLeft={purgeDaysLeft(s)}
                    what={`the song “${s.label}”`}
                    onRestore={() => restoreSong(s.id)}
                    onVaporize={() => vaporizeSong(s)}
                  />
                ) : (
                  <>
                    <div style={{ width: 130, display: 'flex', justifyContent: 'flex-end' }}>
                      <WaveStatus song={s} />
                    </div>
                    {s.storageKey && (
                      <DownloadButton s3Key={s.storageKey} name={s.label} bucket="movies" label="⤓" title="Download song" />
                    )}
                    <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => onPick(s)}>
                      Attach
                    </button>
                    <TrashControls what={`the song “${s.label}”`} onTrash={() => trashSong(s.id)} />
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}
